/**
 * Realtime event ACK queue — the tablet half of the delivery-rate measurement.
 *
 * Every realtime event published by the backend carries a ULID `event_id`.
 * When App.vue applies one, it enqueues the id here; the queue batches them and
 * publishes to the device's own `acks/{deviceId}` channel, where an AppSync
 * handler writes them straight to DynamoDB (bronze-realtime-event-ack).
 * Re-acking the same event_id overwrites the same row — the whole path is
 * idempotent, so this module retries freely and never needs to know whether a
 * failed post actually landed. An ack leaves the queue only when its post was
 * accepted: `events.post` does not throw when AppSync rejects the publish
 * (handler error, unprocessed BatchPutItem items), it returns the failed
 * events, and such a batch stays queued and backs off like a thrown error.
 *
 * Flush policy: 25 ids (the handler's BatchPutItem hard limit) or 30 s after
 * the first pending ack, plus an immediate attempt when the app is hidden /
 * unloaded / back online. Acks are not latency-sensitive (the Fase 2 sweeper
 * looks 5-15 min back), so the cadence favors few, full batches.
 *
 * Persistence is localStorage, not IndexedDB: the pagehide flush can't await
 * an async IndexedDB write (the page may die first), a synchronous setItem of
 * ≤25 tiny objects always completes, and localStorage survives the service
 * worker update that wipes IndexedDB.
 */
import { events } from 'aws-amplify/data';
import Logger from '@/logger';

const logger = new Logger({ screen: 'ackQueue' });

const STORAGE_KEY = 'bronze_pending_acks';
const MAX_BATCH = 25; // acks handler rejects bigger batches (BatchPutItem limit)
const MAX_PENDING = 2000; // long-offline cap: drop oldest instead of growing forever (a resync can ACK a whole 72 h ledger)
const FLUSH_AFTER_MS = 30000;
const RETRY_BASE_MS = 30000;
const RETRY_MAX_MS = 300000;

let deviceId = null;
let restaurantId = null;
let pending = [];
let flushTimer = null;
let flushing = false;
let retryMs = RETRY_BASE_MS;
let listenersArmed = false;

function persist() {
  try {
    // deviceId travels with the acks: leftovers from a session logged in as a
    // DIFFERENT device must be discarded, never posted to the new device's
    // channel (they would pollute the delivery measurement).
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ deviceId, acks: pending }));
  } catch (storageError) {
    logger.console('Could not persist pending acks', storageError);
  }
}

function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.deviceId !== deviceId || !Array.isArray(parsed.acks)) return [];
    return parsed.acks.filter((a) => a && a.event_id);
  } catch (storageError) {
    logger.console('Could not read persisted acks', storageError);
    return [];
  }
}

function clearFlushTimer() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

// Retry the flush later, doubling the wait up to RETRY_MAX_MS; a successful flush resets it.
function backOff() {
  armFlushTimer(retryMs);
  retryMs = Math.min(retryMs * 2, RETRY_MAX_MS);
}

// events.post resolves to undefined when every event was accepted and to the array of failed
// events otherwise. Anything else is unexpected and counts as a rejection: an ack is never
// dropped on a result we can't read.
function publishAccepted(result) {
  return result == null || (Array.isArray(result) && result.length === 0);
}

function armFlushTimer(delayMs) {
  if (flushTimer) return; // timer counts from the FIRST pending ack — don't reset it
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushAcks();
  }, delayMs);
}

// Lifecycle flushes. Enqueue already persisted everything synchronously, so
// these are best-effort attempts to shorten the ack delay — losing the request
// itself is fine (the acks survive in localStorage until the next session).
function onVisibilityChange() {
  if (document.visibilityState === 'hidden') flushAcks();
}
function onPageHide() {
  flushAcks();
}
function onOnline() {
  retryMs = RETRY_BASE_MS; // fresh connection: forget the backoff history
  flushAcks();
}

export function initAckQueue(ids) {
  deviceId = ids.deviceId;
  restaurantId = ids.restaurantId;
  pending = loadPersisted();
  retryMs = RETRY_BASE_MS;

  if (!listenersArmed) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('online', onOnline);
    listenersArmed = true;
  }

  // Leftovers from a previous session (reload mid-batch): flush them soon,
  // but off the login critical path.
  if (pending.length > 0) armFlushTimer(1000);
}

export function enqueueAck({ eventId, channel, via = 'push' }) {
  // `via` records HOW the event reached the tablet: 'push' (live WebSocket) or 'resync'
  // (recovered from the ledger by eventLedger.js) — feeds the %DeliveredByPush metric.
  if (!deviceId || !eventId) return;
  if (pending.some((a) => a.event_id === eventId)) return;

  pending.push({
    event_id: eventId,
    channel: channel || null,
    applied_at: new Date().toISOString(),
    via,
  });
  if (pending.length > MAX_PENDING) {
    pending = pending.slice(pending.length - MAX_PENDING);
  }
  persist();

  if (pending.length >= MAX_BATCH) {
    flushAcks();
  } else {
    armFlushTimer(FLUSH_AFTER_MS);
  }
}

/**
 * Enqueue many ACKs at once (a coalesced resync acks every row it walked). Same item shape and
 * dedup as enqueueAck, but one persist and one flush decision for the whole list.
 * @param {Array<{eventId: string, channel?: string, via?: string}>} items
 */
export function enqueueAcks(items) {
  if (!deviceId || !Array.isArray(items) || items.length === 0) return;
  const known = new Set(pending.map((a) => a.event_id));
  const appliedAt = new Date().toISOString();
  for (const { eventId, channel, via = 'push' } of items) {
    if (!eventId || known.has(eventId)) continue;
    known.add(eventId);
    pending.push({ event_id: eventId, channel: channel || null, applied_at: appliedAt, via });
  }
  if (pending.length > MAX_PENDING) {
    pending = pending.slice(pending.length - MAX_PENDING);
  }
  persist();

  if (pending.length >= MAX_BATCH) {
    flushAcks();
  } else if (pending.length > 0) {
    armFlushTimer(FLUSH_AFTER_MS);
  }
}

export async function flushAcks() {
  if (flushing || !deviceId || pending.length === 0) return;
  flushing = true;
  clearFlushTimer();
  try {
    while (pending.length > 0) {
      const batch = pending.slice(0, MAX_BATCH);
      const failed = await events.post(`acks/${deviceId}`, {
        acks: batch,
        restaurant_id: restaurantId,
      });
      if (!publishAccepted(failed)) {
        // The batch is ONE published event, so any failure is the whole batch: keep it all
        // queued, stop this flush (a rejected batch must not spin the loop) and back off.
        const first = Array.isArray(failed) ? failed[0] : null;
        logger.warning('Ack batch rejected, will retry', {
          batchCount: batch.length,
          pendingCount: pending.length,
          code: first?.code ?? null,
          message: first?.message ?? null,
          retryInMs: retryMs,
        });
        backOff();
        return;
      }
      // Remove what was sent by id: acks enqueued while the post was in flight sit in `pending`
      // too, and the cap may have dropped some of the batch, so a count would delete the wrong ones.
      const sent = new Set(batch.map((a) => a.event_id));
      pending = pending.filter((a) => !sent.has(a.event_id));
      persist();
    }
    retryMs = RETRY_BASE_MS;
  } catch (error) {
    // Keep everything queued and retry with backoff; duplicates on retry are
    // harmless (same DynamoDB row).
    logger.warning('Ack flush failed, will retry', {
      error, pendingCount: pending.length, retryInMs: retryMs,
    });
    backOff();
  } finally {
    flushing = false;
  }
}

export function stopAckQueue() {
  // Best-effort final flush; whatever doesn't make it out stays persisted and
  // is picked up by the next initAckQueue.
  flushAcks();
  clearFlushTimer();
  if (listenersArmed) {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('online', onOnline);
    listenersArmed = false;
  }
}
